import React, { useState, useEffect, useRef } from "react";
import * as d3 from "d3";
import { useDataProvider } from "react-admin";
import moment from "moment";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import { FetchData } from "./dataFetcher";
import { bwColours } from "../lib/theme";

const styles = {
  container: {
    display: "flex",
    // Wrap to a single column on narrow screens: the chart is a fixed
    // 940px canvas, so it can never share a row with the filters on a
    // phone. The chart itself scrolls horizontally inside its box.
    flexWrap: "wrap",
  },
  svgBox: {
    borderRight: "1px solid #d9d9d9",
    height: 400,
    // flex-grow 0: the box must not stretch past the 900px canvas on
    // wide screens (that leaves dead space inside the chart box);
    // flex-shrink 0: the box stays at canvas width until the row
    // wraps, which is what drops the filter panel below the chart on
    // narrow screens. maxWidth caps the box on phones, where the
    // canvas scrolls horizontally inside it.
    flex: "0 0 900px",
    maxWidth: "100%",
    overflowX: "auto",
  },
  svg: {
    // 100% of the box rather than a fixed 400px: overflow-x: auto
    // makes the browser treat overflow-y as auto too, so when the
    // horizontal scrollbar takes vertical space on a phone, a fixed
    // 400px canvas would also get a vertical scrollbar. display:block
    // drops the inline line box, which adds a few px of height —
    // enough to trip a vertical scrollbar in a 400px box.
    height: "100%",
    width: "100%",
    display: "block",
  },
  rightBox: {
    padding: "0 2em",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flex: "1 1 280px",
  },
  filterBox: {
    display: "flex",
    alignItems: "center",
    // Wrap (rather than overflow) when the filter column is squeezed
    // between the 900px chart and the screen edge on narrow screens.
    flexWrap: "wrap",
    gap: "5px",
  },
  filterBoxInner: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
  },
  tooltipBox: {
    height: "100px",
    width: "300px",
    marginBottom: "1em",
    opacity: 0,
  },
  circularProgress: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  lotDropdown: {
    padding: "5px",
    width: "125px",
  },
  timeDropdown: {
    padding: "5px",
    width: "125px",
  },
};

const bwScheme = [
  bwColours.primary.red,
  bwColours.primary.beige,
  bwColours.primary.offblack,
  bwColours.secondary.pink,
  bwColours.secondary.orange,
  bwColours.secondary.lightBlue,
  bwColours.secondary.darkBlue,
  bwColours.accent.purple,
  bwColours.accent.green,
  bwColours.accent.blue,
  bwColours.accent.brown,
  bwColours.accent.pink,
  bwColours.accent.red,
  bwColours.accent.teal,
  bwColours.accent.mauve,
  bwColours.accent.army,
  bwColours.accent.orange,
];

const ChangeTimeFormat = (timeSelected) => {
  // Convert a string selected from the dropdown to a datetime format use a cut-off
  var cutOff = moment().format();

  switch (timeSelected) {
    case "1 Day":
      cutOff = moment().subtract(1, "day").toDate();
      break;
    case "1 Week":
      cutOff = moment().subtract(1, "week").toDate();
      break;
    case "2 Weeks":
      cutOff = moment().subtract(2, "weeks").toDate();
      break;
    case "1 Month":
      cutOff = moment().subtract(1, "month").toDate();
      break;
    case "1 Year":
      cutOff = moment().subtract(1, "year").toDate();
      break;
    case "Auto":
      /*FIXME: this is an arbitrary length of time. The API request requires time to be specified now, but since we don't know
      the earliest date, we just pull the last 3 years of data and figure out the min date when we get the response*/
      cutOff = moment().subtract(3, "year").toDate();
      break;
    default:
    // required for ESLint: https://eslint.org/docs/latest/rules/default-case
  }
  return cutOff;
};

export const GetYMax = (data) => {
  const yMax = Math.max(
    ...data.map((element) => {
      return element.fso2;
    }),
  );

  if (yMax === null || yMax === -Infinity) {
    // if between these two dates, there is no measurements,
    // return 40 as a standard height
    return 38;
  }

  return yMax;
};

const svgSize = {
  width: 940,
  height: 400,
  margintop: 0,
  marginbottom: 30,
  marginleft: 30,
};

function SetScales(windowMinDate, windowMaxDate, yMax) {
  let xScale = d3
    .scaleTime()
    .domain([windowMinDate, windowMaxDate])
    .range([svgSize.marginleft, svgSize.width - svgSize.marginleft])
    .nice();

  let yScale = d3
    .scaleLinear()
    .domain([-3, yMax + 5])
    .range([svgSize.height - svgSize.marginbottom, svgSize.margintop]);

  return { xScale: xScale, yScale: yScale };
}

function SetPointsToCanvas(canvas, data, scales, tooltip) {
  var mouseover = function () {
    tooltip.style("opacity", 1);
  };
  var mousemove = function (d, i) {
    const text = RenderTooltipText(i);

    tooltip
      .html(text)
      .style("left", d3.pointer(this)[0] + 200 + "px")
      .style("top", d3.pointer(this)[1] + "px");
  };
  var mouseleave = function () {
    tooltip.style("opacity", 0);
  };
  // Touch devices have no hover: tapping (or touching) a point shows
  // its details in the tooltip box; tapping the empty chart area
  // (handled on the canvas) hides it again.
  var touchshow = function (event, _d, i) {
    event.stopPropagation();
    tooltip.html(RenderTooltipText(i)).style("opacity", 1);
  };

  var color = d3.scaleOrdinal(bwScheme);

  const lotList = GetLotList(data);

  canvas
    .selectAll("circle")
    .data(data)
    .enter()
    .append("circle")
    .attr("cx", function (d) {
      return scales.xScale(d.datetime);
    })
    .attr("cy", function (d) {
      return scales.yScale(parseFloat(d.fso2));
    })
    .attr("r", 5)
    .style("fill", function (d) {
      if (lotList.length > 34) {
        return bwColours.primary.red;
      } else {
        return color(d.lot);
      }
    })
    .style("opacity", 0.5)
    .attr("clip-path", "url(#clip)")
    .on("mouseover", mouseover)
    .on("mousemove", mousemove)
    .on("mouseleave", mouseleave)
    .on("touchstart", touchshow)
    .on("touchmove", touchshow);
}

export function RenderTooltipText(i) {
  const fso2 = i.fso2;
  const lot = i.lot;
  const barrel = i.alias === "" ? i.barrel : i.alias;
  const datetime = moment(i.datetime).format("llll");

  const text =
    "FSO\u2082: <b>" +
    fso2.toFixed(0) +
    " ppm" +
    "</b><br/>Lot: <b>" +
    lot +
    "</b><br/>Barrel: <b>" +
    barrel +
    "</b><br/>Time: <b>" +
    datetime +
    "</b>";

  return text;
}

const timeOptions = ["Auto", "1 Day", "1 Week", "2 Weeks", "1 Month", "1 Year"];

const TimeDropdown = ({ value, onChange }) => {
  return (
    <select
      value={value}
      id="timeDropdown"
      onChange={onChange}
      style={styles.timeDropdown}
    >
      {timeOptions.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
};

function GetLotList(array) {
  const lotList = [
    ...new Set(
      array.map((x) => {
        return x.lot;
      }),
    ),
  ];
  lotList.sort();
  lotList.unshift("All");
  return lotList;
}

const LotDropdown = ({ value, data, onChange }) => {
  if (!data) return;

  const lotList = GetLotList(data);

  return (
    <select
      value={value}
      id="lotDropdown"
      onChange={onChange}
      style={styles.lotDropdown}
    >
      {lotList.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
};

const SetBrush = (
  canvas,
  scales,
  setWindowMinDate,
  setWindowMaxDate,
  setTimeSelected,
  setBrushed,
) => {
  const { xScale } = scales;

  const brush = d3
    .brushX()
    .extent([
      [0, 0],
      [svgSize.width, svgSize.height],
    ])
    .on("end", brushEnd);

  const brushGroup = canvas.append("g").attr("class", "brush").call(brush);

  function brushEnd(event) {
    const selection = event.selection;
    if (selection === null) {
    } else {
      const mn = xScale.invert(selection[0]);
      const mx = xScale.invert(selection[1]);

      setWindowMinDate(mn);
      setWindowMaxDate(mx);
      setTimeSelected("custom");
      brushGroup.call(brush.move, null);
      setBrushed(true);
      d3.select("#tooltip").style("opacity", 0);
    }
  }
};

const ScatterChart = () => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const dataProvider = useDataProvider();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null); // Store the fetched data
  const defaultSelectedTime = "1 Week";
  const defaultSelectedLot = "All";
  const today = moment().toDate();
  const [timeSelected, setTimeSelected] = useState(defaultSelectedTime);
  const [lotSelected, setLotSelected] = useState(defaultSelectedLot);
  const [windowMinDate, setWindowMinDate] = useState(today);
  const [windowMaxDate, setWindowMaxDate] = useState(
    ChangeTimeFormat(defaultSelectedTime),
  );
  const [resetClicked, setResetClicked] = useState(false);
  const [brushed, setBrushed] = useState(false);
  const svgRef = useRef(null);

  const handleTimeValueChange = (event) => {
    setTimeSelected(event.target.value);
  };
  const handleLotValueChange = (event) => {
    setLotSelected(event.target.value);
  };

  useEffect(() => {
    /*FIXME: when chart is brushed, we change timeSelected to custom, which is not part of the option in the dropdown.
    When reset is clicked, we set time selected back to its default, so this use effect will be triggered.
    It does not seem to be an ideal way of doing this.
    */
    if (timeSelected === "custom") {
      return;
    }

    setWindowMinDate(ChangeTimeFormat(timeSelected));
    setWindowMaxDate(moment().toDate());
  }, [timeSelected]);

  useEffect(() => {
    if (!resetClicked) {
      return;
    }

    if (
      timeSelected === defaultSelectedTime &&
      lotSelected === defaultSelectedLot
    ) {
      return;
    }

    setWindowMinDate(moment().toDate());
    setWindowMaxDate(ChangeTimeFormat(defaultSelectedTime));
    setLotSelected(defaultSelectedLot);
    setTimeSelected(defaultSelectedTime);

    setResetClicked(false);
    setBrushed(false);
  }, [resetClicked, lotSelected, timeSelected]);

  useEffect(() => {
    const apiFilter = {
      valid: true,
      dateRange: {
        timezone: timezone,
        since: windowMinDate,
        until: windowMaxDate,
      },
    };

    if (lotSelected !== defaultSelectedLot) {
      apiFilter.lotName = lotSelected;
    }
    FetchData(
      dataProvider,
      setData,
      setLoading,
      (err) => {
        if (err) console.log(err);
      },
      apiFilter,
    );
  }, [
    windowMinDate,
    windowMaxDate,
    lotSelected,
    dataProvider,
    timezone,
    brushed,
  ]);

  useEffect(() => {
    if (!data) return;
    const yMax = GetYMax(data);

    let canvas = d3.select(svgRef.current);
    canvas.selectAll("*").remove();

    // Define clipping path
    canvas
      .append("defs")
      .append("clipPath")
      .attr("id", "clip")
      .append("rect")
      .attr("x", svgSize.marginleft)
      .attr("width", svgSize.width - svgSize.marginleft)
      .attr(
        "height",
        svgSize.height - svgSize.margintop - svgSize.marginbottom,
      );

    const tooltip = d3.select("#tooltip");

    // Touch devices have no hover: tapping the empty chart area
    // (brush/axes) clears a previously-shown touch tooltip. Taps on
    // the points themselves stop propagation and set it instead.
    canvas.on("touchstart", () => tooltip.style("opacity", 0));

    let scales;
    let minDate = windowMinDate;
    let maxDate = windowMaxDate;
    if (timeSelected === "Auto") {
      const recordMinDate = new Date(
        Math.min(
          ...data.map((element) => {
            return new Date(element.datetime);
          }),
        ),
      );

      const recordMaxDate = new Date(
        Math.max(
          ...data.map((element) => {
            return new Date(element.datetime);
          }),
        ),
      );
      minDate = recordMinDate;
      maxDate = recordMaxDate;
    }

    scales = SetScales(minDate, maxDate, yMax);

    const xAxis = d3.axisBottom().scale(scales.xScale);
    // Add x-axis to the canvas
    canvas
      .append("g")
      .attr("class", "x-axis")
      .attr(
        "transform",
        "translate(0," + (svgSize.height - svgSize.marginbottom) + ")",
      )
      .call(xAxis);

    // Add scale to Y axis
    const yAxis = d3.axisLeft().scale(scales.yScale);
    canvas
      .append("g")
      .attr("class", "y-axis")
      .attr(
        "transform",
        "translate(" + svgSize.marginleft + "," + svgSize.margintop + ")",
      )
      .call(yAxis);

    SetBrush(
      canvas,
      scales,
      setWindowMinDate,
      setWindowMaxDate,
      setTimeSelected,
      setBrushed,
    );
    SetPointsToCanvas(canvas, data, scales, tooltip);
  }, [data, windowMinDate, windowMaxDate, timeSelected, loading]);

  return (
    <div id="container" style={styles.container}>
      <div style={styles.svgBox}>
        {loading ? (
          <div style={styles.circularProgress}>
            <CircularProgress
              sx={{
                color: bwColours.primary.red,
              }}
            />
          </div>
        ) : (
          <svg ref={svgRef} style={styles.svg} />
        )}
      </div>
      <div style={styles.rightBox}>
        <div style={styles.filterBox}>
          <h3>Filters</h3>
          <div style={styles.filterBoxInner}>
            <TimeDropdown
              value={timeSelected}
              onChange={handleTimeValueChange}
            />
            <LotDropdown
              value={lotSelected}
              data={data}
              onChange={handleLotValueChange}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setResetClicked(true);
              }}
              style={{ whiteSpace: "nowrap" }}
            >
              Clear Filter
            </Button>
          </div>
        </div>
        <div id="tooltip" style={styles.tooltipBox}></div>
      </div>
    </div>
  );
};

export default ScatterChart;
